import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { useParceirosAdminControllerCriarUsuario } from '@/api/generated/admin-parceiros/admin-parceiros';
import type { ParceiroAdminResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { toast } from '@/shared/ui/sonner';

/**
 * Cria o usuário do parceiro (B8-5).
 *
 * ⚠️ A senha é definida aqui e entregue **fora de banda** — não há e-mail de
 * verificação até a INF-5 (Resend) fechar. Sem esta tela, o caminho é o da V1:
 * "crie a conta no painel do Supabase e depois ajuste o papel"
 * (`AdminParceiros.tsx:930`).
 *
 * Não há escolha de cargo: é sempre `parceiro`, fixo no servidor. É o que
 * garante que o usuário nasça sem nenhuma permissão clínica.
 */
export function UsuarioDoParceiroDialogo({
  aberto,
  aoFechar,
  parceiro,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parceiro: ParceiroAdminResponse;
}) {
  const criar = useParceirosAdminControllerCriarUsuario();

  const [email, setEmail] = useState('');
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [senha, setSenha] = useState('');
  const [erros, setErros] = useState<Record<string, string[]>>({});

  function salvar(): void {
    setErros({});

    criar.mutate(
      {
        id: parceiro.id,
        data: { email: email.trim(), nomeCompleto: nomeCompleto.trim(), senha },
      },
      {
        onSuccess: () => {
          toast.success('Usuário criado. Entregue a senha ao parceiro por um canal seguro.');
          aoFechar();
        },
        onError: (erro) => {
          if (erro instanceof ApiError && erro.detalhes) {
            setErros(erro.detalhes);
            return;
          }
          toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível criar');
        },
      },
    );
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Usuário de {parceiro.nome}</DialogTitle>
          <DialogDescription>
            Entra no ambiente do parceiro com o cargo <strong>Parceiro</strong> — sem acesso
            clínico. Ele poderá enviar materiais para aprovação.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Campo rotulo="Nome completo" erro={erros.nomeCompleto}>
            <Input value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} />
          </Campo>

          <Campo rotulo="E-mail" erro={erros.email}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Campo>

          <Campo rotulo="Senha provisória" erro={erros.senha}>
            <Input
              type="text"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Ao menos 8 caracteres"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Fica visível de propósito: você precisa copiá-la para entregar ao parceiro.
            </p>
          </Campo>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={salvar}
            disabled={criar.isPending}
            className="bg-brand text-brand-foreground hover:bg-brand/90"
          >
            {criar.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Criar usuário
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Campo({
  rotulo,
  erro,
  children,
}: {
  rotulo: string;
  erro?: string[];
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{rotulo}</Label>
      {children}
      {erro && erro.length > 0 && <p className="text-xs text-destructive">{erro[0]}</p>}
    </div>
  );
}
